import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auditAPI } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const STATUS_CONFIG = {
  ready:    { label: 'Ready',    cls: 'bg-green-100 text-green-700',  dot: 'bg-green-500' },
  pending:  { label: 'Pending',  cls: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700',     dot: 'bg-red-500' },
};

export default function AuditReadiness() {
  const navigate  = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [filter,  setFilter]  = useState('all');
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    auditAPI.list()
      .then((r) => setData(r.data.data))
      .catch(() => setError('Failed to load audit data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner fullPage message="Loading audit report…" />;
  if (error)   return <div className="text-center py-16 text-red-600">{error}</div>;

  const { employees, summary } = data;

  const filtered = employees.filter((emp) => {
    const matchesFilter = filter === 'all' || emp.status === filter;
    const matchesSearch = !search || `${emp.first_name} ${emp.last_name} ${emp.email}`.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="space-y-5">

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SumCard label="Total"          value={summary.total}           color="slate"  icon="👥" />
        <SumCard label="Ready"          value={summary.ready}           color="green"  icon="✅" />
        <SumCard label="Pending"        value={summary.pending}         color="yellow" icon="⏳" />
        <SumCard label="Critical"       value={summary.critical}        color="red"    icon="🚨" />
        <SumCard label="Avg Completion" value={`${summary.avg_completion}%`} color="blue" icon="📊" />
      </div>

      {/* Filters + search */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <input
          type="text"
          placeholder="Search employees…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
        />
        <div className="flex gap-2">
          {['all', 'ready', 'pending', 'critical'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors
                ${filter === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Overall Compliance Progress</h3>
          <span className="text-lg font-bold text-slate-800">{summary.avg_completion}%</span>
        </div>
        <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full progress-bar
              ${summary.avg_completion >= 80 ? 'bg-green-500' :
                summary.avg_completion >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
            style={{ width: `${summary.avg_completion}%` }}
          />
        </div>
        <div className="flex gap-6 mt-4 text-sm">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-slate-600">{summary.ready} Ready</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-yellow-500" /><span className="text-slate-600">{summary.pending} Pending</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500" /><span className="text-slate-600">{summary.critical} Critical</span></div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <p className="text-3xl mb-2">🔍</p>
            <p>No employees match the selected filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Employee', 'Category', 'Completion', 'Docs', 'Issues', 'Status', 'Action'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((emp) => {
                  const cfg = STATUS_CONFIG[emp.status];
                  const issues = [];
                  if (emp.missing_docs  > 0) issues.push(`${emp.missing_docs} missing`);
                  if (emp.expired_docs  > 0) issues.push(`${emp.expired_docs} expired`);
                  if (emp.missing_expiry > 0) issues.push(`${emp.missing_expiry} no expiry`);
                  if (emp.expiring_critical > 0) issues.push(`${emp.expiring_critical} expiring`);

                  return (
                    <tr key={emp.id} className="hoverable">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{emp.first_name} {emp.last_name}</div>
                        <div className="text-xs text-slate-400">{emp.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                          {emp.category.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${emp.completion_pct === 100 ? 'bg-green-500' : emp.completion_pct >= 50 ? 'bg-yellow-500' : 'bg-red-400'}`}
                              style={{ width: `${emp.completion_pct}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-slate-700">{emp.completion_pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {emp.uploaded_docs}/{emp.total_docs}
                      </td>
                      <td className="px-4 py-3">
                        {issues.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {issues.map((iss, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-xs">
                                {iss}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-green-600 text-xs">No issues</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
                            {cfg.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/employees/${emp.id}/documents`)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Review →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SumCard({ label, value, color, icon }) {
  const bg = {
    slate:  'bg-slate-50 border-slate-200',
    green:  'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    red:    'bg-red-50 border-red-200',
    blue:   'bg-blue-50 border-blue-200',
  };
  const tx = {
    slate:  'text-slate-700',
    green:  'text-green-700',
    yellow: 'text-yellow-700',
    red:    'text-red-700',
    blue:   'text-blue-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${bg[color]}`}>
      <div className="text-2xl mb-1">{icon}</div>
      <div className={`text-2xl font-bold ${tx[color]}`}>{value}</div>
      <div className={`text-xs font-medium mt-0.5 ${tx[color]} opacity-80`}>{label}</div>
    </div>
  );
}
